import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { track } from '../../../lib/analytics'
import { claimRationedTip } from '../../../lib/nuxTips'
import type { OneShotTipDismissReason } from '../../../components/OneShotTip'
import type { Artist, PartialRange, Set as FestivalSet } from '../../../types'
import { getNow } from '../../../lib/clock'
import { setLabel } from '../../../lib/setLabel'
import { festivalMinutesToDate, timeToMinutes } from './timeUtils'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'

const TIP = 'set_trim_conflict'
// How long a clashing card has to sit on screen before the tip claims it, so a
// fling through the grid doesn't spend a showing on a card nobody looked at.
const DWELL_MS = 800
// A clash that shows up this soon after tapping a card's heart was caused by
// that tap, so the tip goes on the card that was just tapped.
const TOGGLE_WINDOW_MS = 3000
// The already-there clash is pointed out as the schedule opens, not whenever
// the user later scrolls past it — past this, its card stops reporting.
const OPENING_WINDOW_MS = 3500

function setName(set: FestivalSet, artists: Record<string, Artist>): string {
  return setLabel(set, artists).name
}

interface Window {
  set: FestivalSet
  start: number
  end: number
  /** The whole set — what the card's height spans, trimmed or not. */
  setStart: number
  setEnd: number
}

/** A stretch of a card that clashes, as fractions of the card's height. */
export interface ClashSpan {
  top: number
  bottom: number
}

function spanOf(w: Window, start: number, end: number): ClashSpan {
  const length = w.setEnd - w.setStart
  return { top: (start - w.setStart) / length, bottom: (end - w.setStart) / length }
}

/** A top-to-bottom gradient that paints only the clashing stretches in `warning`. */
export function clashStripe(spans: ClashSpan[]): string {
  const pct = (f: number) => `${(f * 100).toFixed(2)}%`
  const stops: string[] = []
  let prev = 0
  for (const { top, bottom } of spans) {
    stops.push(`transparent ${pct(prev)} ${pct(top)}`, `var(--warning) ${pct(top)} ${pct(bottom)}`)
    prev = bottom
  }
  stops.push(`transparent ${pct(prev)} 100%`)
  return `linear-gradient(to bottom, ${stops.join(', ')})`
}

function mergeSpans(spans: ClashSpan[]): ClashSpan[] {
  const merged: ClashSpan[] = []
  for (const s of [...spans].sort((a, b) => a.top - b.top)) {
    const last = merged[merged.length - 1]
    if (last && s.top <= last.bottom) last.bottom = Math.max(last.bottom, s.bottom)
    else merged.push({ ...s })
  }
  return merged
}

/**
 * Going sets on this day whose windows overlap — each mapped to the name of
 * the first set it clashes with and to the stretches of its card that clash —
 * plus every clashing pair, earlier set first, in order of the earlier set's
 * start. Your own partial ranges count, so a
 * clash you've already trimmed away stops being one.
 */
function findConflicts(
  sets: FestivalSet[],
  cutoff: number,
  goingSetIds: Set<string>,
  ownRanges: ReadonlyMap<string, PartialRange> | undefined,
  artists: Record<string, Artist>,
): { labels: Map<string, string>; spans: Map<string, ClashSpan[]>; pairs: [Window, Window][] } {
  const windows = sets
    .filter(s => goingSetIds.has(s.id))
    .map(set => {
      const setStart = timeToMinutes(set.startTime, cutoff)
      const setEnd = timeToMinutes(set.endTime, cutoff)
      const range = ownRanges?.get(set.id)
      return range
        ? { set, setStart, setEnd, start: setStart + range.startMin, end: setStart + range.endMin }
        : { set, setStart, setEnd, start: setStart, end: setEnd }
    })
    .sort((a, b) => a.start - b.start)
  const labels = new Map<string, string>()
  const rawSpans = new Map<string, ClashSpan[]>()
  const addSpan = (w: Window, start: number, end: number) =>
    rawSpans.set(w.set.id, [...(rawSpans.get(w.set.id) ?? []), spanOf(w, start, end)])
  const pairs: [Window, Window][] = []
  for (let i = 0; i < windows.length; i++) {
    for (let j = i + 1; j < windows.length && windows[j].start < windows[i].end; j++) {
      pairs.push([windows[i], windows[j]])
      const a = windows[i].set
      const b = windows[j].set
      if (!labels.has(a.id)) labels.set(a.id, setName(b, artists))
      if (!labels.has(b.id)) labels.set(b.id, setName(a, artists))
      const start = windows[j].start
      const end = Math.min(windows[i].end, windows[j].end)
      addSpan(windows[i], start, end)
      addSpan(windows[j], start, end)
    }
  }
  const spans = new Map([...rawSpans].map(([id, list]) => [id, mergeSpans(list)]))
  return { labels, spans, pairs }
}

/**
 * The "long-press to resolve a clash" coachmark, in one of two moments:
 * - a heart tap on a card just created a clash → that card, straight away;
 * - the schedule opens on an existing clash → the later set of the earliest
 *   clash that hasn't finished yet (the one that caused it), if its card
 *   settles on screen right as it opens.
 * Either way at most once a session, three times ever — and never again once
 * the user long-presses a set on their own (see `retireRationedTip`).
 * `conflicts` and `clashSpans` are always populated (they also drive the
 * persistent clash marker); `enabled` only gates the coachmark.
 */
export function useTrimConflictTip({
  sets,
  goingSetIds,
  ownRanges,
  artists,
  timezone,
  enabled,
}: {
  sets: FestivalSet[]
  goingSetIds: Set<string>
  ownRanges?: ReadonlyMap<string, PartialRange>
  artists: Record<string, Artist>
  timezone?: string
  enabled: boolean
}) {
  const cutoff = useDayCutoff()
  const { labels: conflicts, spans: clashSpans, pairs } = useMemo(
    () => findConflicts(sets, cutoff, goingSetIds, ownRanges, artists),
    [sets, cutoff, goingSetIds, ownRanges, artists],
  )
  // Read once: which clash is "still ahead" is decided as the schedule opens.
  const [openedAt] = useState(getNow)
  const openingSetId = useMemo(
    () =>
      enabled
        ? (pairs.find(([, later]) => festivalMinutesToDate(later.end, later.set.day, timezone) > openedAt)?.[1]
            .set.id ?? null)
        : null,
    [enabled, pairs, timezone, openedAt],
  )

  const [tipSetId, setTipSetId] = useState<string | null>(null)
  const settled = useRef(false)
  const openRef = useRef<string | null>(null)

  const claim = useCallback((setId: string) => {
    if (settled.current) return
    settled.current = true
    if (!claimRationedTip(TIP)) return
    openRef.current = setId
    setTipSetId(setId)
    track('nux_tip_shown', { tip: TIP })
  }, [])

  const dismiss = useCallback((via: OneShotTipDismissReason) => {
    if (!openRef.current) return
    openRef.current = null
    setTipSetId(null)
    track('nux_tip_dismissed', { tip: TIP, via })
  }, [])

  // The opening window starts when the clash is first known — interests load
  // after the grid, so mount time would already have run out.
  const openingDeadline = useRef<number | null>(null)
  useEffect(() => {
    if (openingSetId && openingDeadline.current === null) {
      openingDeadline.current = Date.now() + OPENING_WINDOW_MS
    }
  }, [openingSetId])
  const onOpeningVisible = useCallback(
    (setId: string) => {
      const deadline = openingDeadline.current
      if (deadline !== null && Date.now() <= deadline) claim(setId)
    },
    [claim],
  )

  const toggled = useRef<{ setId: string; at: number } | null>(null)
  const noteToggle = useCallback((setId: string) => {
    toggled.current = { setId, at: Date.now() }
  }, [])

  // Runs on the snapshot that lands after the tap.
  useEffect(() => {
    if (!enabled) return
    const t = toggled.current
    if (!t || !conflicts.has(t.setId)) return
    toggled.current = null
    if (Date.now() - t.at < TOGGLE_WINDOW_MS) claim(t.setId)
  }, [enabled, conflicts, claim])

  // Trimming (or un-going) either set resolves the clash, which answers the tip.
  const openSetId = enabled && tipSetId && conflicts.has(tipSetId) ? tipSetId : null
  return { conflicts, clashSpans, openingSetId, tipSetId: openSetId, onOpeningVisible, noteToggle, dismiss }
}

/**
 * Calls `onVisible` once the card has stayed mostly on screen for a beat. A
 * long set can be taller than the viewport, so a big enough visible slice
 * counts too.
 */
export function useReportWhenVisible(
  el: RefObject<HTMLElement | null>,
  onVisible: (() => void) | undefined,
) {
  useEffect(() => {
    const node = el.current
    if (!node || !onVisible) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer)
        const shown = entry.intersectionRatio >= 0.6 || entry.intersectionRect.height >= 160
        if (shown) timer = setTimeout(onVisible, DWELL_MS)
      },
      { threshold: [0, 0.25, 0.6, 1] },
    )
    observer.observe(node)
    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
  }, [el, onVisible])
}
